/* HidroVerse — canonical enum maps (Phase A AppSheet migration) */

/* ---------------- Lead phases (12-step funnel + legacy fallbacks) ---------------- */
export const LEAD_PHASE_GROUPS = [
  {
    group: "Open",
    accent: "#1FB6C1",
    phases: [
      { key: "lead_logged", label: "Lead Logged", color: "#647087" },
      { key: "initial_contact", label: "Initial Contact Made", color: "#3B82F6" },
      { key: "callout_quote", label: "Callout Quote Sent", color: "#06B6D4" },
      { key: "callout_scheduled", label: "Callout / Meeting Scheduled", color: "#0EA5E9" },
    ],
  },
  {
    group: "In Progress",
    accent: "#F59E0B",
    phases: [
      { key: "design_quote", label: "Design / Quote In Progress", color: "#8B5CF6" },
      { key: "estimate_sent", label: "Estimate Sent", color: "#F59E0B" },
      { key: "estimate_followup", label: "Estimate Followed Up", color: "#FB923C" },
      { key: "so_sent", label: "Sales Order Sent", color: "#EAB308" },
      { key: "so_followup", label: "S/O Followed Up", color: "#FACC15" },
    ],
  },
  {
    group: "Closed",
    accent: "#A78BFA",
    phases: [
      { key: "lead_paused", label: "Lead Paused (Follow up later)", color: "#6366F1" },
      { key: "won", label: "Lead Converted (Won)", color: "#10B981" },
      { key: "lost", label: "Lead Lost", color: "#F43F5E" },
    ],
  },
];

export const LEAD_PHASE_LABELS = LEAD_PHASE_GROUPS.flatMap((g) => g.phases).reduce(
  (acc, p) => ({ ...acc, [p.key]: p.label }),
  {},
);

// Legacy phase keys kept available so we can render historical data
export const LEGACY_LEAD_PHASE_LABELS = {
  new_lead: "New Lead",
  meeting_scheduled: "Meeting scheduled",
  design_in_progress: "Design in progress",
  quote_in_progress: "Quote in progress",
  quote_sent: "Quote sent",
  sales_order_sent: "Sales order sent",
  future_prospect: "Future prospect",
  new: "New",
  contacted: "Contacted",
  qualified: "Qualified",
  proposal: "Proposal",
};

/* ---------------- Lead sources (verbatim from AppSheet — preserve typos & trailing space) ---------------- */
export const LEAD_SOURCES = [
  "Website",
  "Walk In ", // trailing space preserved
  "Referral",
  "Cold Calling (Telephone)",
  "Email",
  "Trade Show",
  "Social Media",
  "Existing Customer",
  "Other",
  "Unknown",
];

/* ---------------- Wave 4: Price brackets (ZAR) ---------------- */
export const PRICE_BRACKETS = [
  { key: "0-100k", label: "R0 – R100k" },
  { key: "100-250k", label: "R100k – R250k" },
  { key: "250-500k", label: "R250k – R500k" },
  { key: "500-750k", label: "R500k – R750k" },
  { key: "750k-1m", label: "R750k – R1M" },
  { key: "1m-1.5m", label: "R1M – R1.5M" },
  { key: ">1.5m", label: "More than R1.5M" },
];
export const PRICE_BRACKET_LABELS = PRICE_BRACKETS.reduce(
  (acc, b) => ({ ...acc, [b.key]: b.label }),
  {},
);

/* ---------------- Wave 4: Lead title enum ---------------- */
export const LEAD_TITLES = [
  "Cold call",
  "Purification",
  "Pumps",
  "Pool",
  "WWTP",
  "Irrigation",
  "Automation",
  "Borehole",
  "RO Plant",
  "Filtration",
  "Softening",
  "Tank install",
  "Maintenance contract",
  "Site survey",
  "Quote only",
  "Other",
];


/* ---------------- Job-card phases (12 approved, no legacy) ---------------- */
// [Iter 336 — 2026-02-25] Removed legacy phases entirely per Dirk's
// request. Historical JCs are migrated on first read via the
// `_LEGACY_PHASE_MIGRATION` map in `backend/routes/jobcards.py` so
// nothing renders as "Other / legacy" any more.
//
//   Legacy → Approved mapping:
//     complete        → on_site_complete
//     paid            → closed
//     quote_client    → created
//
// The 12 approved phases below form the ONE source of truth for
// dropdowns, kanban columns, filters, PDF export, and reporting.
export const JOBCARD_PHASES = [
  { key: "created", label: "Job Card Created", color: "#647087" },
  { key: "picking", label: "Picking / Ordering", color: "#F59E0B" },
  { key: "ready", label: "Ready For Site", color: "#06B6D4" },
  { key: "scheduled", label: "Scheduled", color: "#3B82F6" },
  { key: "in_progress", label: "In Progress", color: "#1FB6C1" },
  { key: "paused", label: "Paused", color: "#A78BFA" },
  { key: "on_site_complete", label: "On-Site Completed", color: "#10B981" },
  { key: "signed_off", label: "Sign-off Completed", color: "#8B5CF6" },
  { key: "ready_to_invoice", label: "Ready To Invoice", color: "#EAB308" },
  { key: "invoice_sent", label: "Invoice Sent", color: "#FACC15" },
  { key: "closed", label: "Closed", color: "#475569" },
  { key: "cancelled", label: "Cancelled", color: "#F43F5E" },
];

// Phases shown in CREATE forms — all approved phases (no legacy filter needed).
export const JOBCARD_PHASES_CREATE = JOBCARD_PHASES;

// Phases shown in EDIT dropdown — same list as CREATE.
export const JOBCARD_PHASES_EDIT = JOBCARD_PHASES;

/* ---------------- Required skills (technician roles) ---------------- */
export const REQUIRED_SKILLS = [
  "Driver",
  "General",
  "Plumber",
  "Electrician",
  "Pump Tech",
  "RO Tech",
  "Filtration Tech",
  "Sales",
  "Installer",
  "Service Tech",
];

export const JOBCARD_PHASE_LABELS = JOBCARD_PHASES.reduce(
  (acc, p) => ({ ...acc, [p.key]: p.label }),
  {},
);

/* ---------------- Ticket statuses ---------------- */
export const TICKET_STATUSES = [
  { key: "logged", label: "Logged", color: "#F59E0B" },
  { key: "pending_assignment", label: "Pending Assignment", color: "#FB923C" },
  { key: "assigned", label: "Assigned", color: "#06B6D4" },
  { key: "in_progress", label: "In Progress", color: "#1FB6C1" },
  { key: "escalated", label: "Escalated", color: "#F43F5E" },
  { key: "job_card_issued", label: "Job Card Issued", color: "#A78BFA" },
  { key: "resolved_via_jc", label: "Resolved via Job Card", color: "#10B981" },
  { key: "resolved_external", label: "Resolved Externally", color: "#10B981" },
  // Iter 418.au (Dirk, 2026-02-28) — remote / phone / portal fix
  // where no on-site work was needed. Counts as closed.
  { key: "resolved_virtually", label: "Resolved Virtually", color: "#10B981" },
  { key: "cancelled", label: "Cancelled", color: "#647087" },
];

export const TICKET_STATUS_LABELS = TICKET_STATUSES.reduce(
  (acc, s) => ({ ...acc, [s.key]: s.label }),
  {},
);

/* ---------------- Ticket support types (Excel enum) ---------------- */
export const TICKET_SUPPORT_TYPES = [
  { key: "on_site_technical", label: "On-site technical" },
  { key: "team_callout", label: "Team callout" },
  { key: "virtual_assistance", label: "Virtual assistance" },
];
export const TICKET_SUPPORT_TYPE_LABELS = TICKET_SUPPORT_TYPES.reduce(
  (acc, s) => ({ ...acc, [s.key]: s.label }),
  {},
);

/* ---------------- Task recurrence ---------------- */
export const TASK_RECURRENCE_TYPES = [
  { key: "once", label: "Once" },
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
];

export const DAYS_OF_WEEK = [
  { key: 0, label: "Mon" },
  { key: 1, label: "Tue" },
  { key: 2, label: "Wed" },
  { key: 3, label: "Thu" },
  { key: 4, label: "Fri" },
  { key: 5, label: "Sat" },
  { key: 6, label: "Sun" },
];

/* ---------------- Customer status enum ---------------- */
export const CUSTOMER_STATUSES = [
  { key: "lead", label: "Lead", color: "#A78BFA" },
  { key: "active", label: "Active", color: "#10B981" },
  { key: "inactive", label: "Inactive", color: "#647087" },
  { key: "closed", label: "Closed", color: "#475569" },
  { key: "lost", label: "Lost", color: "#F43F5E" },
];

/* ---------------- Inventory categories (used for Equipment + Components master) ---------------- */
export const INVENTORY_CATEGORIES = [
  { key: "general", label: "General" },
  { key: "filter", label: "Filter" },
  { key: "pump", label: "Pump" },
  { key: "membrane", label: "Membrane" },
  { key: "chemical", label: "Chemical" },
  { key: "tank", label: "Tank" },
  { key: "consumable", label: "Consumable" },
  { key: "component", label: "Component" },
];
